import { Injectable, inject, signal, computed } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';

import { coreSelectors } from 'app/core/state';
import { Timeout } from 'app/types';
import { closeRtcPeer } from 'app/utils/media/rtc';
import {
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessageType,
  RecordingType,
  StartRecordingMessage,
  EndRecordingMessage,
  UpdateType,
  ScreenShare,
  PeerJsep,
  PeerCandidate,
} from 'messages/websocket.messages';

import { StageSizeType } from '../../performer-participant/performer-participant.types';
import ParticipantRtcStats from '../../preciate-stream/participant-rtc-stats';
import { MediaStreamWrapper } from '../../preciate-stream/room-participant';
import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import { SubscriptionState } from '../../types/webrtc';
import { HeaderToastMessageService } from '../header-toast-message.service';
import { RtcStatsStreamService, StatsCounter } from '../rtc-stats-stream';
import { SceneService } from '../scene.service';
import { GroupUser, SubscriptionReason, SubscriptionType } from '../subscription/subscription.types';
import { LoadingTransitionLabel, TransitionService } from '../transition.service';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

import { GROUP_SCREEN_SHARE_SUBSCRIPTION_ID, MOVEMENT_INTERVAL, PARTICIPANT_SCREEN_SHARE_ENDED_MESSAGE } from './types';
import { toObservable } from '@angular/core/rxjs-interop';
import moment from 'moment';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  takeUntil,
  combineLatest,
  throttle,
  interval,
  startWith,
  pairwise,
} from 'rxjs';
import { GroupShareScreenBaseService, ScreenShareStatus } from './group-share-screen-base.service';
import { GroupShareScreenMessageService } from './group-share-screen-message.service';

export const EMPTY_STREAM = { id: uuidv4(), stream: undefined };

export enum GroupShareState {
  NOT_SHARING = 'Share Screen to Group',
  NOT_SHARING_ENHANCED = 'Share Video Stream to Group',
  SHARING = 'End share',
  SHARING_ENHANCED = 'End video share',
  INVITED = 'Join group share',
  INVITED_ENHANCED = 'Join video group share',
  JOINED = 'Leave group',
}
@Injectable({
  providedIn: 'root',
})
export class GroupShareScreenParticipantService extends GroupShareScreenBaseService {
  protected override readonly websocketService = inject(WebsocketService);
  protected override readonly sceneService = inject(SceneService);
  protected override readonly headerToastMessageService = inject(HeaderToastMessageService);

  private videoRoomService = inject(VideoRoomService);
  private rtcStatsStreamService = inject(RtcStatsStreamService);
  private transitionService = inject(TransitionService);
  private messageService = inject(GroupShareScreenMessageService);

  private oldScreenSharingUserId?: string;
  private readonly sessionId = signal<string | undefined>(undefined);
  private shareSubscriptionState: SubscriptionState = SubscriptionState.NOT_SUBSCRIBED;

  // Group Parameters
  public readonly groupShareIds = signal<string[]>([]);
  private group: GroupUser[] = [];
  private oldGroup: GroupUser[] = [];

  // Recording
  private recordingId?: string;
  private recordingInProgress = false;

  private _peer?: RTCPeerConnection;
  private peerConnectionTime?: number;
  private retryTimeout?: Timeout;
  private _subscriptionState: SubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
  private rtcStats?: ParticipantRtcStats;
  private rtcVideoStatsInterval?: Timeout;
  private processingOffer = false;

  private mediaStreamSource = new BehaviorSubject<MediaStreamWrapper>(EMPTY_STREAM);
  public mediaStream$ = this.mediaStreamSource.asObservable();

  public readonly groupShareStartedSignal = signal<boolean>(false);

  public readonly isGroupShareActiveSignal = signal<boolean>(false);

  // Using signal as the single source of truth
  public readonly groupShareId = signal<string>('');
  // Observable for backward compatibility - create as property instead of getter
  public readonly groupShareId$: Observable<string>;

  public readonly viewSize = signal<StageSizeType>(StageSizeType.MEDIUM);

  // private is the joined group share id, boolean for public access
  // Using both signal and BehaviorSubject for backward compatibility during migration
  private readonly _userHasJoinedShare = signal<string | undefined>(undefined);
  private _userHasJoinedShare$ = new BehaviorSubject<boolean>(false);
  public userHasJoinedShare$ = this._userHasJoinedShare$.asObservable();

  public readonly hasMembersInScreenShare = signal(false);
  private screenShareInGroup?: ScreenShare;
  public readonly isGroupShareFull = signal(false);

  protected isPresenter(): boolean {
    return this.userSignal()?.userId === this.screenSharingUserId();
  }

  public readonly userHasJoinedShare = computed((): boolean => {
    return !!this._userHasJoinedShare();
  });

  public setUserHasJoinedShare(value: boolean | string): void {
    // Handle joining share
    if (value) {
      // Handle string value (direct share ID) or use existing groupShareId
      const shareId = typeof value === 'string' ? value : this.groupShareId();

      // Safety check - ensure we have a valid shareId
      if (!shareId) {
        this.error('Cannot join share - no valid group share ID available');
        return;
      }

      this._userHasJoinedShare.set(shareId);
      this._userHasJoinedShare$.next(true);

      // Make sure groupShareId is in sync
      if (this.groupShareId() !== shareId) {
        this.groupShareId.set(shareId);
      }

      // Set the group share as active
      this.isGroupShareActiveSignal.set(true);

      // Don't add presenter as participant
      if (this.userSignal()?.userId !== shareId) {
        this.requestAddGroupScreenParticipant(shareId);
      }
      return;
    }

    // Handle leaving share
    if (this._userHasJoinedShare() && this.userSignal()?.userId !== this.groupShareId()) {
      this.requestRemoveGroupScreenParticipant();
    }

    this._userHasJoinedShare.set(undefined);
    this._userHasJoinedShare$.next(false);
  }

  private requestAddGroupScreenParticipant(groupShareId: string): void {
    // Multiple validation checks to ensure we have valid data
    if (!groupShareId || groupShareId.trim() === '') {
      this.error('Cannot add group share participant, groupShareId is not set');
      return;
    }

    const userId = this.userSignal()?.userId;
    if (!userId) {
      this.error('Cannot add group share participant, userId is not set');
      return;
    }

    // Make sure state is consistent
    this.screenSharingUserId.set(groupShareId);
    this.isGroupShareActiveSignal.set(true);

    // Add this log to help with debugging
    this.info('Adding group share participant', {
      groupShareId,
      userId,
      isActive: this.isGroupShareActiveSignal(),
    });

    // Request to add participant
    SocialStateMachine.shared
      .requestAddGroupScreenParticipant({
        groupShareId,
        userId: userId,
        updateType: UpdateType.CURRENT,
      })
      .then(() => {
        this.info('Successfully added group screen participant');
      })
      .catch((e) => {
        this.error('Error adding group share participant', e);
      });

    // Create peer connection
    this.createPeerWhenReady();
  }
  private requestRemoveGroupScreenParticipant(): void {
    const groupShareId = this.groupShareId();
    if (!groupShareId) {
      this.error('Cannot remove group share participant, groupShareId is not set');
      return;
    }

    SocialStateMachine.shared
      .requestRemoveGroupScreenParticipant({
        groupShareId,
        userId: this.userSignal()?.userId ?? '',
        updateType: UpdateType.CURRENT,
      })
      .catch((e) => this.error('Error removing group share participant', e));
  }

  public readonly groupShareState = computed((): GroupShareState => {
    // return if group share is not active
    if (!this.isGroupShareActiveSignal()) {
      return GroupShareState.NOT_SHARING;
    }
    if (this.isHost()) {
      return GroupShareState.SHARING;
    }
    if (this.userHasJoinedShare()) {
      return GroupShareState.JOINED;
    }
    return GroupShareState.INVITED;
  });

  public readonly groupShareEnhancedState = computed((): GroupShareState => {
    // return if group share is not active
    if (!this.isGroupShareActiveSignal()) {
      return GroupShareState.NOT_SHARING_ENHANCED;
    }
    if (this.isHost()) {
      return GroupShareState.SHARING_ENHANCED;
    }
    if (this.userHasJoinedShare()) {
      return GroupShareState.JOINED;
    }
    return GroupShareState.INVITED_ENHANCED;
  });

  public constructor() {
    super();
    this.setDisplayName('GroupShareScreenParticipantService');

    this.groupShareId$ = toObservable(this.groupShareId);

    this.websocketService.message$.subscribe(this.wsMessageListener);

    // Social state
    this.store.select(socialAppSelectors.getSocialSessionId).subscribe((sessionId) => this.sessionId.set(sessionId));
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject<boolean>(1);

    // User state
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => {
        this.userSignal.set(user);
      });

    // Group Shares
    this.store
      .select(socialAppSelectors.getGroupShareIds)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ids) => {
        this.groupShareIds.set(ids);
        if (!ids || !ids.length) {
          return;
        }
        this.handleGroupShare();
        this.handleScreenShareLeftGroup();
      });

    this.store
      .select(socialAppSelectors.getGroupScreenShares)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((screenShares) => {
        if (!screenShares || !this.screenSharingUserId()) {
          this.screenShareInGroup = undefined;
          this.isGroupShareFull.set(false);
          return;
        }

        this.screenShareInGroup = screenShares[this.screenSharingUserId() || ''];

        this.isGroupShareFull.set(
          !!(
            this.screenShareInGroup &&
            this.screenShareInGroup.viewerUserIds &&
            this.sceneService.maxGroupSize &&
            this.screenShareInGroup.viewerUserIds.length + 1 >= this.sceneService.maxGroupSize
          ),
        );
      });

    // Event state
    this.store
      .select(socialAppSelectors.isEventActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        if (!this.screenSharingUserId()) {
          return;
        }
        // If event is active display banner
        const ids = this.group.map((user) => user.userId);
        if (active && ids.includes(this.screenSharingUserId() || '')) {
          this.displayBannerMessage(PARTICIPANT_SCREEN_SHARE_ENDED_MESSAGE);
        }
      });

    combineLatest([
      this.sceneService.primaryGroupMembers$.pipe(
        throttle(() => interval(MOVEMENT_INTERVAL), { leading: true }),
        startWith([]),
        pairwise(),
        takeUntil(this.destroyed$),
      ),
      this.store.select(socialAppSelectors.getMyGroupScreenShare),
      this.store.select(socialAppSelectors.getGroupScreenShareUsers),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([[oldGroup, group], myGroupScreenShare, groupScreenShareUsers]) => {
        // do not update if user is in active group sharer
        if (!myGroupScreenShare) {
          this.oldGroup = oldGroup.map(({ userId, visible }) => ({ userId, visible }));
          this.group = group.map(({ userId, visible }) => ({ userId, visible }));
          if (this.group.length === 0) {
            return;
          }
          this.handleGroupShare();
        }

        // are any members of the group in a screen share?
        this.hasMembersInScreenShare.set(
          group.some(
            (user) => groupScreenShareUsers.includes(user.userId) && user.userId !== this.screenSharingUserId(),
          ),
        );

        // don't send these if presenter
        if (this.isPresenter()) {
          return;
        }

        if (myGroupScreenShare && myGroupScreenShare.recordingId && !this.recordingInProgress) {
          this.recordingId = myGroupScreenShare?.recordingId;
          this.startParticipantRecording();
        }

        if (myGroupScreenShare && !myGroupScreenShare.recordingId && this.recordingInProgress) {
          this.endParticipantRecording();
        }
      });
  }

  // Override base class's updateHostStatus method
  protected override updateHostStatus(): void {
    const isCurrentUserHost = this.screenSharingUserId() === this.userSignal()?.userId;
    this.isHost.set(isCurrentUserHost);
  }

  // Starts share for participant
  private handleGroupShare(): void {
    // Check to see if user is a part of a group with a ScreenShare
    // If true, create peer
    if (!this.screenSharingUserId()) {
      this.group.forEach((user) => {
        if (this.groupShareIds()?.includes(user.userId)) {
          this.screenSharingUserId.set(user.userId);

          this.isGroupShareActiveSignal.set(true);
          this.groupShareStartedSignal.set(true);
          this.groupShareId.set(user.userId);

          this.setScreenShareStatus(ScreenShareStatus.ACTIVE, {
            userId: user.userId,
            reason: 'handleGroupShare detected active share',
          });

          this.updateHostStatus();
          this.createPeerWhenReady();
        }
      });
    }

    // Check if current subscribed screenShare is part of group
    // If not, clear screen share
    const ids = this.group.map((user) => user.userId);
    if (this.screenSharingUserId()) {
      if (!this.groupShareIds()?.includes(this.screenSharingUserId()!) || !ids.includes(this.screenSharingUserId()!)) {
        this.clear();
      }
    }
  }

  private peerConnectionTimeoutId?: ReturnType<typeof setTimeout>;

  private createPeerWhenReady(): void {
    // Don't recreate peer if we're already subscribed or in the process of connecting
    if (
      this._subscriptionState === SubscriptionState.SUBSCRIBED ||
      this._subscriptionState === SubscriptionState.SUBSCRIBING
    ) {
      this.info('Peer connection already active or in progress');
      return;
    }

    // Set state to show we're initiating connection
    this._subscriptionState = SubscriptionState.SUBSCRIBING;

    // Clear any existing timeout
    if (this.peerConnectionTimeoutId) {
      clearTimeout(this.peerConnectionTimeoutId);
    }

    // Try immediately first for better performance
    this.info('Creating peer connection immediately');
    this.createPeer();

    // Also set a fallback timeout just in case the immediate attempt fails silently
    this.peerConnectionTimeoutId = setTimeout(() => {
      // Only retry if we're still in SUBSCRIBING state
      if (this._subscriptionState === SubscriptionState.SUBSCRIBING) {
        this.info('Retrying peer connection after timeout');
        this.reconnectPeer();
      }
    }, 7000); // Increased to 7 seconds to avoid conflicting with standard WebRTC timeouts
  }

  private handleScreenShareLeftGroup(): void {
    // Display event message if screen sharer left group
    const groupIds = this.group.map((user) => user.userId);
    const oldGroupIds = this.oldGroup.map((user) => user.userId);
    const userId = this.userSignal()?.userId ?? '';
    // Do not show message if user was screen sharer
    if (this.oldScreenSharingUserId === userId) {
      return;
    }
    // Do not show message if group shares still include screen share
    if (this.oldScreenSharingUserId && this.groupShareIds()?.includes(this.oldScreenSharingUserId)) {
      return;
    }
    // Check if user left group
    if (
      this.oldScreenSharingUserId &&
      !groupIds.includes(this.oldScreenSharingUserId) &&
      oldGroupIds.includes(this.oldScreenSharingUserId)
    ) {
      this.displayBannerMessage(PARTICIPANT_SCREEN_SHARE_ENDED_MESSAGE);
      this.oldScreenSharingUserId = undefined;
    }
  }

  // -- Group Share Recording --------------------------------------------------------------------------------------
  private startParticipantRecording(): void {
    if (!this.recordingId) {
      this.error('Attempted to start a participant group share recording without recording id.');
      return;
    }

    const userId = this.userSignal()?.userId ?? '';

    this.info('Starting group share participant recording', { recordingId: this.recordingId });
    const pubMsg: StartRecordingMessage = {
      t: OutgoingMessageType.START_RECORDING,
      recordingId: this.recordingId,
      type: RecordingType.PUBLISHER,
      groupShareUserId: userId,
    };
    this.websocketService.sendMessage(pubMsg);
    this.recordingInProgress = true;
  }

  private endParticipantRecording(): void {
    this.info('Stopping group share participant recording', { recordingId: this.recordingId });

    const userId = this.userSignal()?.userId ?? '';

    const pubMsg: EndRecordingMessage = {
      t: OutgoingMessageType.END_RECORDING,
      type: RecordingType.PUBLISHER,
      groupShareUserId: userId,
    };
    this.recordingInProgress = false;
    this.recordingId = undefined;
    this.websocketService.sendMessage(pubMsg);
  }

  // -- CREATE and CLEAR Peer --------------------------------------------------------------------------------------

  public clear(): void {
    const wasActive = this.isGroupShareActiveSignal();
    const oldScreenSharingUserId = this.screenSharingUserId();

    // Reset all state
    this.shareSubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
    this.isGroupShareActiveSignal.set(false);
    this.groupShareStartedSignal.set(false);
    this.groupShareId.set('');
    this.processingOffer = false;

    this.setScreenShareStatus(ScreenShareStatus.NONE, { reason: 'clear_called' });

    // End Recording for Participant
    if (this.recordingInProgress && !this.isPresenter()) {
      this.endParticipantRecording();
    }

    // Send end sharing message if we were the host
    // if (wasActive && this.isHost() && oldScreenSharingUserId) {
    //   this.info('Sending end sharing message as host', { userId: oldScreenSharingUserId });

    //   // Send end sharing message with the user's social ID
    //   this.messageService.sendEndSharingMessage(oldScreenSharingUserId);
    // }

    // Clear subscription from store
    this.store.dispatch(socialAppActions.REMOVE_SUBSCRIPTION({ subscriptionId: GROUP_SCREEN_SHARE_SUBSCRIPTION_ID }));

    // Clear any pending timeouts
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = undefined;
    }

    if (this.peerConnectionTimeoutId) {
      clearTimeout(this.peerConnectionTimeoutId);
      this.peerConnectionTimeoutId = undefined;
    }

    // Save old ID for potential messages
    this.oldScreenSharingUserId = oldScreenSharingUserId;
    this.screenSharingUserId.set(undefined);
    this.updateHostStatus(); // Update isHost when screenSharingUserId changes
    this._userHasJoinedShare.set(undefined);
    this._userHasJoinedShare$.next(false);

    // Clean up RTC stats
    this.destroyRtcStats();

    // Close peer connection if it exists
    if (!this.peer) {
      return;
    }

    this.info('Closing peer connection', { userId: this.oldScreenSharingUserId });

    // Send unsubscribe message if we have a user ID
    if (this.oldScreenSharingUserId) {
      try {
        this.messageService.sendUnsubscribeMessage(this.oldScreenSharingUserId);
      } catch (e) {
        this.error('Error Unsubscribing to group screen share: ', e);
      }
    }

    closeRtcPeer(this.peer);
    this.peer = undefined;
  }

  public destroy(): void {
    this.clear();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // Websocket Message Listener
  private wsMessageListener = async (msg: IncomingMessage): Promise<void> => {
    const { t } = msg;
    switch (t) {
      case IncomingMessageType.SHR_STARTED: {
        this.info('ShareScreenParticipant message received', msg);
        const { groupShare, userId } = msg;

        // Only handle group share messages
        if (!groupShare) {
          return;
        }

        // Set proper subscription state
        this.shareSubscriptionState = SubscriptionState.SUBSCRIBED; // Set consistent state
        if (this.groupShareIds()?.includes(userId)) {
          // Update active state
          this.isGroupShareActiveSignal.set(true);

          // Ensure we have the correct sharing user id
          if (!this.screenSharingUserId() || this.screenSharingUserId() !== userId) {
            this.screenSharingUserId.set(userId);
            this.updateHostStatus(); // Update isHost when screenSharingUserId changes
          }

          // Make sure groupShareId is in sync
          if (this.groupShareId() !== userId) {
            this.groupShareId.set(userId);
          }
        }

        // Should we auto join?
        const inGroupForShare = !!this.sceneService.primaryGroupMemberSubject.value?.filter((p) => p.userId === userId)
          .length;
        const hasWarning = !!this.sceneService.primaryGroupMemberSubject.value?.filter(
          (p) => p.userId === this.userSignal()?.userId && p.groupWarning,
        ).length;
        this.isGroupShareActiveSignal.set(true);

        if (groupShare && !this.userHasJoinedShare() && inGroupForShare && !hasWarning) {
          this.info('Auto-joining group share', { userId, groupShareIds: this.groupShareIds() });
          this.transitionService
            .enableDarkModeEffect(LoadingTransitionLabel.GROUPSHARE, true, true)
            .then(() => {
              // Pass the userId directly to ensure we have a valid value
              this.setUserHasJoinedShare(userId);
            })
            .catch((err) => {
              this.error('Failed to execute transition', err);
            });
        }

        this.handleStartMessage(userId);
        return;
      }

      case IncomingMessageType.SHR_ENDED: {
        this.info('ShareScreen ended message received', msg);
        const { userId } = msg;

        // Update subscription state
        this.shareSubscriptionState = SubscriptionState.NOT_SUBSCRIBED;

        // If this is for our active screen share, clear it
        if (userId === this.screenSharingUserId()) {
          this.info('Clearing group share as it has ended', { userId });
          this.clear();
        }
        return;
      }

      case IncomingMessageType.SHR_SUBOFF: {
        const { d, groupShare } = msg;
        if (groupShare) {
          // Skip if we're already processing an offer to prevent redundant connections
          if (this.processingOffer) {
            this.info('Skipping redundant offer while already processing one', {
              processingOffer: this.processingOffer,
              sharingUserId: this.screenSharingUserId(),
            });
            return;
          }

          this.info('GroupShareScreenParticipant message received', msg);
          this.processingOffer = true;

          try {
            await this.setDescription(d as RTCSessionDescriptionInit);
          } finally {
            // Reset the flag when processing is complete, whether successful or not
            this.processingOffer = false;
          }
        }
        return;
      }
    }
  };

  private handleStartMessage(userId: string): void {
    // Make sure we track this ID regardless of group membership
    if (this.groupShareIds()?.includes(userId)) {
      // Set consistent state
      this.groupShareStartedSignal.set(true);
      this.isGroupShareActiveSignal.set(true);

      this.setScreenShareStatus(ScreenShareStatus.ACTIVE, { userId });

      // Only update the screen sharing user ID if not already set
      if (!this.screenSharingUserId()) {
        this.screenSharingUserId.set(userId);
        this.updateHostStatus(); // Update isHost when screenSharingUserId changes
      }

      // Make sure groupShareId is in sync
      if (this.groupShareId() !== userId) {
        this.groupShareId.set(userId);
      }
    }
  }

  private createPeer(): void {
    if (!this.sessionId()) {
      this.error('Not Valid:: sessionId', { sessionId: this.sessionId() });
      return;
    }

    if (!this.screenSharingUserId()) {
      this.error('Group Screen sharing user id is undefined, cannot subscribe to screen share');
      return;
    }

    // Create Peer Connection
    this.info('[1] Creating Peer ', this.screenSharingUserId);

    // Setup WebRTC Peer and Tracks
    const rtcConfig = this.videoRoomService.createRtcConfig();
    this.peer = new RTCPeerConnection(rtcConfig);
    this.peerConnectionTime = Date.now();

    this.peer.ontrack = (event) => this.onTrack(event);
    this.peer.onicecandidate = (event) => this.sendCandidate(event);
    this.peer.oniceconnectionstatechange = (): void => {
      const { iceConnectionState } = this.peer || {};

      this.info('Subscribe ICE state change', { iceConnectionState, peerTime: this.peerConnectionTime });
    };
    this.peer.onicecandidateerror = (e) => this.info('Candidate error', this.screenSharingUserId, e);

    this.peer.onicegatheringstatechange = (): void => {
      const { iceGatheringState } = this.peer || {};
      this.info('Subscribe ICE gathering state change', { iceGatheringState, peerTime: this.peerConnectionTime });
    };

    this.peer.onconnectionstatechange = () => {
      // Peer may have already been destroyed. There's nothing to do in this case.
      if (!this.peer) {
        return;
      }
      const { connectionState } = this.peer;
      this.info('Subscribe state change', { connectionState });
      switch (connectionState) {
        case 'connected':
          this.subscriptionState = SubscriptionState.SUBSCRIBED;
          this.startRtcStats();
          break;

        case 'failed':
          this.error('Peer connection failed', { connectionState });
          this.subscriptionState = SubscriptionState.FAILED;

          this.handleConnectionFailed();
          break;

        default:
          break;
      }
    };

    // Notification Status
    this.isGroupShareActiveSignal.set(true);
    this.groupShareId.set(this.screenSharingUserId()!);
    this.oldScreenSharingUserId = undefined;

    this.messageService.sendSubscribeMessage(this.screenSharingUserId()!);
  }

  private reconnectPeer(): void {
    if (!this.screenSharingUserId()) {
      this.warn('Could not reconnect peer, group screen share user id is undefined');
      return;
    }

    // Close existing peer if there is one
    if (this.peer) {
      closeRtcPeer(this.peer);
      this.peer = undefined;
    }

    // Reset processing flag to allow new connection attempt
    this.processingOffer = false;
    this.setScreenShareStatus(ScreenShareStatus.LOADING, { reason: 'Reconnecting peer' });

    // Update subscription state
    this.subscriptionState = SubscriptionState.RESUBSCRIBE;

    // Create a new peer
    this.createPeer();
  }

  private async setDescription(offer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peer) {
      this.error('Could not set description for RTCPeer connection, peer is not set');
      return;
    }

    try {
      this.info('[2] Setting Description ', this.screenSharingUserId);
      await this.peer.setRemoteDescription(offer);
      this.peer.getTransceivers().forEach((t) => (t.direction = 'recvonly'));
      const answer = await this.peer.createAnswer();
      await this.peer.setLocalDescription(answer);

      if (!answer.type) {
        this.error('setDescription: answer type is unexpectedly undefined', answer);
        return;
      }

      if (!this.screenSharingUserId()) {
        this.error('Could not send SharingSubscribeAnswerMessage, group screen sharing user id is not set');
        return;
      }

      this.messageService.sendSubscribeAnswerMessage(this.screenSharingUserId()!, answer as PeerJsep);

      // Update state to show we're waiting for connection completion
      this.subscriptionState = SubscriptionState.SUBSCRIBING;
    } catch (error) {
      this.error('Error in setDescription', error);
      this.subscriptionState = SubscriptionState.FAILED;
      this.retrySubscription();
    }
  }

  private onTrack(event: RTCTrackEvent): void {
    if (event?.track?.kind !== 'video') {
      return;
    }
    this.info('[3] On Track Video ', this.screenSharingUserId);
    const stream = event.streams[0];
    this.mediaStreamSource.next({ id: uuidv4(), stream });
  }

  private sendCandidate(event: RTCPeerConnectionIceEvent): void {
    const { candidate } = event;
    if (!candidate) {
      this.info('ICE Candidate was null, done');
      return;
    }

    this.info('ICE Candidate received', candidate.toJSON());
    if (candidate && this.screenSharingUserId()) {
      this.messageService.sendSubscriberCandidate(this.screenSharingUserId()!, candidate as PeerCandidate);
    }
  }

  private handleConnectionFailed(): void {
    this.rtcStatsStreamService.incrementCounter(StatsCounter.SCREENSHARE_SUBSCRIBER_CONNECTION_FAILED);
    const oneMinuteAgo = moment().subtract(1, 'minute');
    const connectionTime = this.peerConnectionTime;

    this.setScreenShareStatus(ScreenShareStatus.ERROR, {
      connectionTime,
      reason: 'Connection failed',
    });

    // If the connection fails in under a minute, or we never connected, advance the reconnection state machine to
    // potentially more reliable methods
    if (!connectionTime || connectionTime > oneMinuteAgo.valueOf()) {
      this.videoRoomService.advanceReconnectionState({ connectionTime });
    }

    this.retrySubscription();
  }

  private retrySubscription(): void {
    this.subscriptionState = SubscriptionState.RESUBSCRIBE;
    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = undefined;
      this.reconnectPeer();
    }, 1500);
  }

  // -- RTC STats --------------------------------------------------------------------------------------
  private startRtcStats(): void {
    this.info('RTC Stats START for group share participant');
    const rtcStats = (this.rtcStats = new ParticipantRtcStats(''));
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
    }
    this.rtcVideoStatsInterval = setInterval(async () => {
      // Validate if peer and videoTrack is still available
      let isVideoStuck = false;

      if (!this.peer) {
        return;
      }

      const stats = await this.peer.getStats();
      isVideoStuck = rtcStats.isVideoStuck(stats, 'groupshare', 'participant');

      if (isVideoStuck) {
        this.warn('Group share participant Media is Stuck RESUBSCRIBE', {
          isVideoStuck,
        });
        this.destroyRtcStats();
        this.handleConnectionFailed();
      }
    }, 1000);
  }

  private destroyRtcStats(): void {
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
    }
    if (this.rtcStats) {
      this.info('Group share RTC Stats STOP');
    }
    this.rtcStats?.destroy();
    this.rtcVideoStatsInterval = undefined;
    this.rtcStats = undefined;
  }

  private set subscriptionState(state: SubscriptionState) {
    this.info(`Subscription state ${this._subscriptionState} -> ${state}`);
    this._subscriptionState = state;
    this.store.dispatch(
      socialAppActions.CHANGE_SUBSCRIPTION_STATUS({
        status: {
          name: GROUP_SCREEN_SHARE_SUBSCRIPTION_ID,
          id: GROUP_SCREEN_SHARE_SUBSCRIPTION_ID,
          state,
          videoNeeded: true,
          audioNeeded: false,
          reason: SubscriptionReason.SCREEN_SHARE,
          type: SubscriptionType.SCREEN_SHARE,
          peerTime: 0,
        },
      }),
    );
  }

  public get peer(): RTCPeerConnection | undefined {
    return this._peer;
  }

  private set peer(newPeer: RTCPeerConnection | undefined) {
    if (newPeer) {
      this.rtcStatsStreamService.addSubscriptionMonitoring(
        GROUP_SCREEN_SHARE_SUBSCRIPTION_ID,
        SubscriptionType.MIX,
        newPeer,
      );
    } else {
      this.rtcStatsStreamService.removeSubscriptionMonitoring(GROUP_SCREEN_SHARE_SUBSCRIPTION_ID);
    }
    this._peer = newPeer;
  }

  // #region Test Utilities

  /**
   * Simulate peer close
   * @param userId Close the peer for this userId
   */
  public closeSubscriberPeer(): void {
    if (!this.peer) {
      this.warn('Subscriber peer not found');
      return;
    }

    this.subscriptionState = SubscriptionState.FAILED;
    closeRtcPeer(this.peer);
  }
  // #endregion
}
